import React, { useRef, useEffect, useState } from 'react';
import ArabicKeyboard from './ArabicKeyboard';
import { useI18n } from '../../i18n';
import './SearchBar.css';

const KeyboardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <line x1="6" y1="8" x2="6" y2="8.01" />
    <line x1="10" y1="8" x2="10" y2="8.01" />
    <line x1="14" y1="8" x2="14" y2="8.01" />
    <line x1="18" y1="8" x2="18" y2="8.01" />
    <line x1="6" y1="12" x2="6" y2="12.01" />
    <line x1="10" y1="12" x2="10" y2="12.01" />
    <line x1="14" y1="12" x2="14" y2="12.01" />
    <line x1="18" y1="12" x2="18" y2="12.01" />
    <line x1="8" y1="16" x2="16" y2="16" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ReplaceIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 1l4 4-4 4" />
    <path d="M3 11V9a4 4 0 014-4h14" />
    <path d="M7 23l-4-4 4-4" />
    <path d="M21 13v2a4 4 0 01-4 4H3" />
  </svg>
);

const SearchBar = ({
  isOpen,
  searchQuery,
  replaceQuery,
  showReplace,
  matches,
  currentMatchIndex,
  matchCase,
  useRegex,
  wholeWord,
  onClose,
  onSearchChange,
  onReplaceChange,
  onNextMatch,
  onPrevMatch,
  onReplaceCurrent,
  onReplaceAll,
  onToggleMatchCase,
  onToggleRegex,
  onToggleWholeWord,
  onToggleReplace,
  onKeyDown
}) => {
  const { t } = useI18n();
  const searchInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const [showSearchKeyboard, setShowSearchKeyboard] = useState(false);
  const [showReplaceKeyboard, setShowReplaceKeyboard] = useState(false);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
      searchInputRef.current.select();
    }
  }, [isOpen]);

  // Close keyboards when search bar is closed
  useEffect(() => {
    if (!isOpen) {
      setShowSearchKeyboard(false);
      setShowReplaceKeyboard(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const matchCount = matches.length;
  const currentDisplay = matchCount > 0 ? currentMatchIndex + 1 : 0;

  const handleSearchKeyboardClose = () => {
    setShowSearchKeyboard(false);
  };

  const handleReplaceKeyboardClose = () => {
    setShowReplaceKeyboard(false);
  };

  return (
    <div className="search-bar-container">
      <div className="search-bar">
        {/* Search Row */}
        <div className="search-row">
          <div className="search-icon">
            <SearchIcon />
          </div>
          
          <input
            ref={searchInputRef}
            type="text"
            className="search-input"
            placeholder={t('Search...')}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={onKeyDown}
            dir="auto"
          />

          {/* Keyboard Toggle for Search */}
          <button
            className={`keyboard-toggle ${showSearchKeyboard ? 'active' : ''} tooltip-trigger`}
            onClick={() => {
              setShowSearchKeyboard(!showSearchKeyboard);
              setShowReplaceKeyboard(false);
            }}
            data-tooltip={t('Toggle Arabic Keyboard')}
            aria-label={t('Toggle Arabic Keyboard')}
          >
            <KeyboardIcon />
          </button>
          
          {/* Match Count */}
          <div className="match-count">
            {searchQuery ? (
              matchCount > 0 ? (
                <span>{t('{current} of {total}', { current: currentDisplay, total: matchCount })}</span>
              ) : (
                <span className="no-matches">{t('No matches')}</span>
              )
            ) : null}
          </div>

          {/* Option Toggles */}
          <div className="search-options">
            <button
              className={`option-btn ${matchCase ? 'active' : ''} tooltip-trigger`}
              onClick={onToggleMatchCase}
              data-tooltip={t('Match Case')}
              aria-label={t('Match Case')}
            >
              Aa
            </button>
            <button
              className={`option-btn ${wholeWord ? 'active' : ''} tooltip-trigger`}
              onClick={onToggleWholeWord}
              data-tooltip={t('Whole Word')}
              aria-label={t('Whole Word')}
            >
              W
            </button>
            <button
              className={`option-btn regex ${useRegex ? 'active' : ''} tooltip-trigger`}
              onClick={onToggleRegex}
              data-tooltip={t('Use Regular Expression')}
              aria-label={t('Use Regular Expression')}
            >
              .*
            </button>
          </div>

          {/* Navigation */}
          <div className="search-nav">
            <button
              className="nav-btn tooltip-trigger"
              onClick={onPrevMatch}
              disabled={matchCount === 0}
              data-tooltip={t('Previous Match (Shift+Enter)')}
              aria-label={t('Previous Match')}
            >
              <ChevronUpIcon />
            </button>
            <button
              className="nav-btn tooltip-trigger"
              onClick={onNextMatch}
              disabled={matchCount === 0}
              data-tooltip={t('Next Match (Enter)')}
              aria-label={t('Next Match')}
            >
              <ChevronDownIcon />
            </button>
          </div>

          {/* Replace Toggle */}
          <button
            className={`replace-toggle ${showReplace ? 'active' : ''} tooltip-trigger`}
            onClick={onToggleReplace}
            data-tooltip={t('Toggle Replace')}
            aria-label={t('Toggle Replace')}
          >
            <ReplaceIcon />
          </button>

          {/* Close */}
          <button className="close-btn tooltip-trigger" onClick={onClose} data-tooltip={t('Close (Esc)')} aria-label={t('Close')}>
            <CloseIcon />
          </button>
        </div>

        {/* Search Keyboard */}
        {showSearchKeyboard && (
          <div className="keyboard-panel">
            <ArabicKeyboard
              value={searchQuery}
              onChange={onSearchChange}
              onClose={handleSearchKeyboardClose}
            />
          </div>
        )}

        {/* Replace Row */}
        {showReplace && (
          <div className="replace-row">
            <div className="replace-icon">
              <ReplaceIcon />
            </div>
            
            <input
              ref={replaceInputRef}
              type="text"
              className="replace-input"
              placeholder={t('Replace with...')}
              value={replaceQuery}
              onChange={(e) => onReplaceChange(e.target.value)}
              onKeyDown={onKeyDown}
              dir="auto"
            />

            {/* Keyboard Toggle for Replace */}
            <button
              className={`keyboard-toggle ${showReplaceKeyboard ? 'active' : ''} tooltip-trigger`}
              onClick={() => {
                setShowReplaceKeyboard(!showReplaceKeyboard);
                setShowSearchKeyboard(false);
              }}
              data-tooltip={t('Toggle Arabic Keyboard')}
              aria-label={t('Toggle Arabic Keyboard')}
            >
              <KeyboardIcon />
            </button>

            <div className="replace-actions">
              <button
                className="replace-btn tooltip-trigger"
                onClick={onReplaceCurrent}
                disabled={matchCount === 0}
                data-tooltip={t('Replace Current')}
                aria-label={t('Replace Current')}
              >
                {t('Replace')}
              </button>
              <button
                className="replace-btn replace-all tooltip-trigger"
                onClick={onReplaceAll}
                disabled={matchCount === 0}
                data-tooltip={t('Replace All')}
                aria-label={t('Replace All')}
              >
                {t('Replace All')}
              </button>
            </div>
          </div>
        )}

        {/* Replace Keyboard */}
        {showReplace && showReplaceKeyboard && (
          <div className="keyboard-panel">
            <ArabicKeyboard
              value={replaceQuery}
              onChange={onReplaceChange}
              onClose={handleReplaceKeyboardClose}
            />
          </div>
        )}
      </div>

      {/* Keyboard Shortcuts Hint */}
      <div className="search-hints">
        <span>{t('Enter: Next')}</span>
        <span>{t('Shift+Enter: Previous')}</span>
        <span>{t('Esc: Close')}</span>
      </div>
    </div>
  );
};

export default SearchBar;
